import type { RouteRecordRaw, RouteComponent } from "vue-router";

import { USER_ACTIONS } from "hoxton-shared/enums/user";

import Clients from "@/views/Clients.vue";
import CampaignsDashboard from "@/views/CampaignsDashboard.vue";
import FontsDashboard from "@/views/FontsDashboard.vue";
import UserNotFound from "@/views/UserNotFound.vue";
import Maintenance from "@/views/Maintenance.vue";
import CampaignEdit from "@/views/Campaign/Edit.vue";
import CampaignLibrary from "@/views/Campaign/Library.vue";
import CampaignTemplates from "@/views/Campaign/MasterTemplates.vue";
import Connect from "@/Connect/views/Connect.vue";

import store from "@/store";
import { MediaLibraryAction } from "@/store/modules/mediaLibrary";

import { AppRoute, AppRouteNames } from "./constants";

export const privateRoutes: RouteRecordRaw[] = [
    {
        path: AppRoute.HOME,
        name: AppRouteNames.HOME,
        component: Clients as RouteComponent
    },
    {
        path: AppRoute.CLIENTS,
        name: AppRouteNames.CLIENTS,
        redirect: { name: AppRouteNames.HOME }
    },
    {
        path: AppRoute.MAINTENANCE,
        name: AppRouteNames.MAINTENANCE,
        component: Maintenance as RouteComponent
    },
    {
        path: AppRoute.SANDBOX,
        name: AppRouteNames.SANDBOX,
        component: CampaignEdit as RouteComponent,
        meta: { theme: "campaign-light", hideCampaignNav: true },
        props: { sandboxMode: true }
    },
    {
        path: AppRoute.USER_NOT_FOUND,
        name: AppRouteNames.USER_NOT_FOUND,
        component: UserNotFound as RouteComponent
    },
    {
        path: AppRoute.CLIENT_CAMPAIGNS,
        meta: { breadcrumb: ":client" },
        beforeEnter: async (to, _, next) => {
            const clientId = to.params.clientId as string;
            const client = store.state.ui.currentClient;

            // If there is no current client set OR the id in to.params is different to that of the current client then we are switching
            if (client?._id !== clientId) {
                await store.dispatch(MediaLibraryAction.ResetState);
                // TODO: Clear apollo cache? Clear other states?
            }

            next();
        },
        children: [
            {
                path: "",
                name: AppRouteNames.CLIENT_CAMPAIGNS,
                component: CampaignsDashboard as RouteComponent
            },
            {
                path: AppRoute.CLIENT_MASTER_TEMPLATES,
                name: AppRouteNames.CLIENT_MASTER_TEMPLATES,
                component: CampaignTemplates as RouteComponent,
                meta: {
                    theme: "templates-light",
                    breadcrumb: "Master Templates",
                    permission: {
                        action: USER_ACTIONS.canListMasterTemplates,
                        scope: {
                            clientId: true
                        }
                    }
                }
            },
            {
                path: AppRoute.CLIENT_FONTS,
                name: AppRouteNames.CLIENT_FONTS,
                component: FontsDashboard as RouteComponent,
                meta: {
                    permission: {
                        action: USER_ACTIONS.canManageFonts,
                        scope: {
                            clientId: true
                        }
                    }
                }
            },
            {
                path: AppRoute.CAMPAIGN,
                meta: {
                    breadcrumb: ":campaign",
                    permission: {
                        action: USER_ACTIONS.canManageOverwrites,
                        scope: {
                            clientId: true,
                            campaignId: true
                        }
                    }
                },
                children: [
                    {
                        path: "",
                        name: AppRouteNames.CAMPAIGN,
                        redirect: { name: AppRouteNames.CAMPAIGN_LIBRARY }
                    },
                    {
                        path: AppRoute.CAMPAIGN_EDITOR,
                        name: AppRouteNames.CAMPAIGN_EDITOR,
                        component: CampaignEdit as RouteComponent,
                        meta: {
                            theme: "campaign-light",
                            breadcrumb: "Assemble"
                        }
                    },
                    {
                        path: AppRoute.CAMPAIGN_LIBRARY,
                        name: AppRouteNames.CAMPAIGN_LIBRARY,
                        component: CampaignLibrary as RouteComponent,
                        meta: {
                            theme: "campaign-light",
                            breadcrumb: "Creative Library"
                        }
                    },
                    {
                        path: AppRoute.CAMPAIGN_CONNECT,
                        name: AppRouteNames.CAMPAIGN_CONNECT,
                        component: Connect as RouteComponent,
                        meta: {
                            theme: "campaign-light",
                            breadcrumb: "Connect"
                        }
                    }
                ]
            }
        ]
    }
] as const;
